import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Factory as FactoryIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useRegistration } from '../context/RegistrationContext';
import { alpha, useTheme } from '@mui/material/styles';
import apiService from '../services/api';
import PricingEstimator from './PricingEstimator';
import MarketingHeading from './shared/MarketingHeading';
import ScrollableTableContainer from './shared/ScrollableTableContainer';
import {
  annualSavingsPercent,
  BillingInterval,
  FALLBACK_PUBLIC_PRICING,
  FEATURE_COMPARISON_ROWS,
  formatInr,
  formatPlanLimitsLine,
  PLAN_FEATURE_HIGHLIGHTS,
  planTierKey,
  plansForBillingInterval,
  PRICING_FAQ,
  MSME_PRICING_CONTACT_EMAIL,
  PRICING_TEASER,
  PublicFixedPlan,
  PublicPricingData,
  TIER_DISPLAY_NAMES,
} from '../constants/pricingPlans';
import { SUBSCRIPTION_PLANS_ENABLED } from '../constants/subscriptionPlans';

const renderComparisonCell = (value: boolean | string) => {
  if (value === true) {
    return <CheckIcon fontSize="small" color="success" />;
  }
  if (value === false) {
    return <CloseIcon fontSize="small" color="disabled" />;
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {value}
    </Typography>
  );
};

const PricingPage: React.FC = () => {
  const theme = useTheme();
  const { isRegistered } = useRegistration();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState<PublicPricingData>(FALLBACK_PUBLIC_PRICING);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');

  const fetchPricing = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = (await apiService.getPublicPricingPlans()) as {
        success?: boolean;
        data?: PublicPricingData;
      };
      if (response?.success && response.data?.fixedPlans?.length) {
        setPricing(response.data);
      } else {
        setPricing(FALLBACK_PUBLIC_PRICING);
      }
    } catch {
      setLoadError('Showing standard plan catalog. Live pricing will load when the API is available.');
      setPricing(FALLBACK_PUBLIC_PRICING);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const contactOnly =
    pricing.msmePricingContactOnly ??
    FALLBACK_PUBLIC_PRICING.msmePricingContactOnly ??
    true;
  const contactEmail =
    pricing.msmePricingContactEmail ??
    FALLBACK_PUBLIC_PRICING.msmePricingContactEmail ??
    MSME_PRICING_CONTACT_EMAIL;

  const allPlans = pricing.fixedPlans;
  const displayPlans = useMemo(
    () => plansForBillingInterval(allPlans, billingInterval),
    [allPlans, billingInterval]
  );
  const paymentMethodLabels = useMemo(() => {
    const labels: string[] = [];
    if (pricing.paymentMethods?.upi) labels.push('UPI');
    if (pricing.paymentMethods?.cards) labels.push('Cards');
    if (pricing.paymentMethods?.netBanking) labels.push('Net banking');
    return labels.length > 0 ? labels.join(', ') : 'UPI, cards, and net banking (when enabled by admin)';
  }, [pricing.paymentMethods]);

  const renderPlanCard = (plan: PublicFixedPlan) => {
    const tierKey = planTierKey(plan);
    const tierName = TIER_DISPLAY_NAMES[tierKey];
    const highlights = PLAN_FEATURE_HIGHLIGHTS[tierKey] || [];
    const isPopular =
      tierKey === 'growth' ||
      plan.isPopular ||
      plan.planId === PRICING_TEASER.popularPlanId;
    const isAnnualOnly = tierKey === 'scale';
    const savingsPct =
      !contactOnly && billingInterval === 'yearly' ? annualSavingsPercent(allPlans, tierKey) : null;
    const intervalLabel = plan.interval === 'yearly' ? 'per year' : 'per month';
    const limitsLine = formatPlanLimitsLine(plan.limits);
    const inclGst = plan.amountInrInclGst ?? (plan.amountInr > 0 ? Math.round(plan.amountInr * 1.18) : 0);
    const isFree = plan.amountInr <= 0 || plan.requiresPayment === false;

    return (
      <Grid item xs={12} sm={6} lg={4} key={plan.planId}>
        <Card
          variant="outlined"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderColor: isPopular
              ? alpha(theme.palette.primary.main, 0.55)
              : alpha(theme.palette.divider, 0.9),
            boxShadow: isPopular ? theme.shadows[4] : undefined,
            position: 'relative',
          }}
        >
          {isPopular && (
            <Chip
              label="Most popular"
              color="primary"
              size="small"
              sx={{ position: 'absolute', top: 12, right: 12 }}
            />
          )}
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: isPopular ? 4.5 : 2 }}>
            <Typography variant="overline" color="text.secondary">
              MSME
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
              {tierName}
            </Typography>
            {isAnnualOnly && billingInterval === 'monthly' && (
              <Chip label="Annual billing only" size="small" variant="outlined" sx={{ mb: 0.5, alignSelf: 'flex-start' }} />
            )}
            {savingsPct != null && savingsPct > 0 && (
              <Chip
                label={`Save ~${savingsPct}% vs monthly`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ mb: 0.5, alignSelf: 'flex-start' }}
              />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
              {plan.description}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {isFree ? 'Free' : contactOnly ? 'Contact for pricing' : formatInr(plan.amountInr, pricing.currency)}
            </Typography>
            {!contactOnly && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  {intervalLabel}
                  {!isFree && pricing.pricesExclusiveOfGst ? ' · excl. 18% GST' : ''}
                </Typography>
                {!isFree && inclGst > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    ≈ {formatInr(inclGst, pricing.currency)} incl. GST
                  </Typography>
                )}
              </>
            )}
            {contactOnly && !isFree && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Email {contactEmail} for a quote
              </Typography>
            )}
            {limitsLine && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                {limitsLine}
              </Typography>
            )}
            <List dense disablePadding sx={{ mb: 2, flex: 1 }}>
              {highlights.map((item) => (
                <ListItem key={item} disableGutters sx={{ py: 0.35 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
            <Button
              component={contactOnly && !isFree ? 'a' : RouterLink}
              href={contactOnly && !isFree ? `mailto:${contactEmail}?subject=MSME%20plan%20pricing%20inquiry` : undefined}
              to={
                contactOnly && !isFree
                  ? undefined
                  : isRegistered
                    ? '/settings?tab=billing'
                    : `/register?type=msme&plan=${encodeURIComponent(plan.planId)}`
              }
              variant={isPopular ? 'contained' : 'outlined'}
              fullWidth
              endIcon={<ArrowForwardIcon />}
              sx={
                isPopular
                  ? {
                      background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                      },
                    }
                  : undefined
              }
            >
              {contactOnly && !isFree
                ? 'Email for pricing'
                : isRegistered
                  ? (isFree ? 'Current free access' : 'Contact billing')
                  : isFree
                    ? 'Start free'
                    : 'Get started'}
            </Button>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.18)}`,
          background: isDark
            ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`
            : 'linear-gradient(135deg, rgba(76, 175, 80, 0.07) 0%, rgba(46, 125, 50, 0.1) 100%)',
        }}
      >
        <Chip label="MSME plans" size="small" color="primary" variant="outlined" sx={{ mb: 2 }} />
        <MarketingHeading level="h1" sx={{ mb: 1.5 }}>
          {PRICING_TEASER.headline}
        </MarketingHeading>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 820, mb: 2 }}>
          {PRICING_TEASER.subline}
        </Typography>
        {loadError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}
        {(pricing.informationalOnly || contactOnly) && (
          <Alert severity="info" icon={<PaymentsIcon />} sx={{ maxWidth: 900 }}>
            {contactOnly
              ? `MSME plan pricing is available on request. Email ${contactEmail} for a quote tailored to your sites, users, and document volume.`
              : pricing.billingNote}
          </Alert>
        )}
        {!SUBSCRIPTION_PLANS_ENABLED ? (
          <Alert severity="warning" sx={{ mt: 2, maxWidth: 900 }}>
            Checkout and plan enforcement are currently off for this workspace. Growth-tier capabilities
            (compliance, BRSR, targets, finance, credits) remain available without a paid plan or document
            quota. The compare table below is planned commercial packaging, not a live entitlement gate.
          </Alert>
        ) : null}
      </Paper>

      <PricingEstimator contactOnly={contactOnly} contactEmail={contactEmail} />

      <Box component="section" sx={{ mb: 5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 1 }}
        >
          <Box>
            <MarketingHeading level="h2" sx={{ mb: 0.5 }}>
              MSME plans
            </MarketingHeading>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              {contactOnly
                ? 'Five tiers—Free, Starter, Core, Growth, and Scale. Compare capabilities below; email us for pricing.'
                : 'Five tiers—Free, Starter, Core, Growth, and Scale. Toggle billing cycle below; annual plans bill once per year with savings on Starter, Core, and Growth.'}
            </Typography>
          </Box>
          {!contactOnly && (
            <FormControlLabel
              control={
                <Switch
                  checked={billingInterval === 'yearly'}
                  onChange={(_, checked) => setBillingInterval(checked ? 'yearly' : 'monthly')}
                  color="primary"
                />
              }
              label={billingInterval === 'yearly' ? 'Annual billing' : 'Monthly billing'}
              sx={{ m: 0, flexShrink: 0 }}
            />
          )}
        </Stack>
        <Alert severity="success" variant="outlined" sx={{ mb: 3, maxWidth: 900 }}>
          {contactOnly
            ? `Recommended: fixed tier plans for most MSMEs. Email ${contactEmail} for tier pricing or a usage-based quote for seasonal volume and document overage.`
            : `Recommended: fixed tier plans for most MSMEs. Usage-based quotes are a separate model for seasonal volume and document overage (from ${formatInr(Math.max(Number(pricing.usagePricing.baseAmountInr) || 0, Number(pricing.usagePricing.starterFloorInr) || 1999), pricing.currency)} (Starter floor)).`}
        </Alert>
        <Grid container spacing={2}>
          {displayPlans.map(renderPlanCard)}
        </Grid>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 5,
          borderRadius: 2,
          borderColor: alpha(theme.palette.primary.main, 0.2),
        }}
      >
        <MarketingHeading level="h2" sx={{ mb: 1 }}>
          Usage-based pricing (overage & seasonal)
        </MarketingHeading>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
          {pricing.usagePricing.summary}
        </Typography>
        {!contactOnly && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Starting from
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {formatInr(
                  Math.max(
                    Number(pricing.usagePricing.baseAmountInr) || 0,
                    Number(pricing.usagePricing.starterFloorInr) || 1999
                  ),
                  pricing.currency
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Typical range
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatInr(pricing.usagePricing.typicalRangeInr.min, pricing.currency)}
                {' – '}
                {formatInr(pricing.usagePricing.typicalRangeInr.max, pricing.currency)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Floor (excl. GST)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatInr(pricing.usagePricing.starterFloorInr ?? 1999, pricing.currency)}
              </Typography>
            </Grid>
            {pricing.usagePricing.overagePerDocumentInr != null && (
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Document overage
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatInr(pricing.usagePricing.overagePerDocumentInr, pricing.currency)} / doc
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
        <Grid container spacing={1.5}>
          {pricing.usagePricing.factors.map((factor) => (
            <Grid item xs={12} sm={6} key={factor.key}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.04),
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {factor.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {factor.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Button
          component={contactOnly ? 'a' : RouterLink}
          href={contactOnly ? `mailto:${contactEmail}?subject=MSME%20usage-based%20pricing%20inquiry` : undefined}
          to={contactOnly ? undefined : isRegistered ? '/settings?tab=billing' : '/register?type=msme'}
          variant="outlined"
          sx={{ mt: 2.5 }}
          startIcon={<FactoryIcon />}
        >
          {contactOnly
            ? `Email ${contactEmail}`
            : isRegistered
              ? 'Contact billing for a usage quote'
              : 'Register to see your quote'}
        </Button>
      </Paper>

      <Box component="section" sx={{ mb: 5 }}>
        <MarketingHeading level="h2" sx={{ mb: 2 }}>
          Compare plan capabilities
        </MarketingHeading>
        {!SUBSCRIPTION_PLANS_ENABLED ? (
          <Alert severity="info" sx={{ mb: 2, maxWidth: 900 }}>
            Feature locks in this table describe the intended Growth packaging. They are not enforced while
            subscription checkout is disabled.
          </Alert>
        ) : null}
        <ScrollableTableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Free</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Starter</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Core</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Growth</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Scale</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {FEATURE_COMPARISON_ROWS.map((row) => {
                const isPriceRow = row.label === 'Price';
                const priceCells = isPriceRow && billingInterval === 'yearly'
                  ? {
                      free: 'Free',
                      starter: '₹19,990/yr',
                      core: '₹34,990/yr',
                      growth: '₹54,990/yr',
                      scale: '₹1,29,990/yr',
                    }
                  : null;
                return (
                <TableRow key={row.label}>
                  <TableCell>{row.label}{isPriceRow && billingInterval === 'yearly' ? ' (annual)' : isPriceRow ? ' (monthly)' : ''}</TableCell>
                  <TableCell align="center">{renderComparisonCell(priceCells?.free ?? row.free)}</TableCell>
                  <TableCell align="center">{renderComparisonCell(priceCells?.starter ?? row.starter)}</TableCell>
                  <TableCell align="center">{renderComparisonCell(priceCells?.core ?? row.core)}</TableCell>
                  <TableCell align="center">{renderComparisonCell(priceCells?.growth ?? row.growth)}</TableCell>
                  <TableCell align="center">{renderComparisonCell(priceCells?.scale ?? row.scale)}</TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
      </Box>

      <Box component="section" sx={{ mb: 4 }}>
        <MarketingHeading level="h2" sx={{ mb: 2 }}>
          Frequently asked questions
        </MarketingHeading>
        <Stack spacing={2}>
          {PRICING_FAQ.map((item) => (
            <Paper key={item.question} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                {item.question}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.answer}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Payments accepted: {paymentMethodLabels}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prices are exclusive of GST unless stated on your invoice.
            {pricing.moduleEnabled ? '' : ' · Billing can be enabled by your administrator'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button component={RouterLink} to="/terms-of-service" size="small">
            Terms
          </Button>
          <Button component={RouterLink} to="/privacy-policy" size="small">
            Privacy
          </Button>
          <Button component={RouterLink} to="/register?type=msme" variant="contained" size="large">
            Create MSME account
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PricingPage;
